import React from 'react';

import Trustpilot from '@rover/components/src/Trustpilot';
import { Box, Display, Flex, Heading } from '@rover/kibble/core';
import Connect from '@rover/react-lib/src/components/utils/Redux/Connect';
import roverHomepageHeroDesktop from '@rover/react-lib/src/images/homepage/rover_homepage_hero_desktop.webp';
import roverHomepageHeroTablet from '@rover/react-lib/src/images/homepage/rover_homepage_hero_tablet.webp';
import useInGroomingPilot from '@rover/react-lib/src/pages/grooming/hooks/useInGroomingPilot';
import DogTrainingPilotCard, {
  useShouldShowDogTrainingCard,
} from '@rover/react-lib/src/pages/home/DogTrainingPilotCard';
import GroomingPilotCard from '@rover/react-lib/src/pages/home/GroomingPilotCard';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useHomePageConfigurationShim from '@rover/shared/js/hooks/useHomePageConfigurationShim';
import useRebookingShim from '@rover/shared/js/hooks/useRebookingShim';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';

import { selectors as homePageSelectors } from '../HomePage.duck';
import ServiceTypeDescription from '../ServiceTypeDescription';

import NarrowDeviceSearchBoxContainer from './IntuitiveOwnerWizard/NarrowDeviceSearchBoxContainer';
import SearchBoxContainer from './SearchBox/SearchBoxContainer';
import Rebooking from './Rebooking';

type Props = {
  petTypeBrowsableServiceTypeOptions: ServiceTypeDescription[];
};

const HomePageHeroContent: React.FC<Props> = ({ petTypeBrowsableServiceTypeOptions }) => {
  const { headlineText, subHeadlineText } = useHomePageConfigurationShim();
  const { isAuthenticated } = useCurrentUserShim();
  const { language } = useFrontendConfigurationShim();
  const rebookingProfiles = useRebookingShim();
  const { serviceLinksForCurrentRoute } = useServiceLinksShim();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();
  const shouldShowDogTrainingCard = useShouldShowDogTrainingCard();

  const hasRebookingProfiles =
    isAuthenticated && rebookingProfiles != null && rebookingProfiles.length > 0;

  return (
    <Box data-qa-id="home-page-hero" position="relative">
      {/* Background image section with overlay */}
      <Box
        position="relative"
        backgroundImage={[
          `url(${roverHomepageHeroTablet})`,
          `url(${roverHomepageHeroTablet})`,
          `url(${roverHomepageHeroDesktop})`,
        ]}
        backgroundSize="cover"
        backgroundPosition="top"
        sx={{ overflow: 'hidden' }}
      >
        {/* Headline content */}
        <Flex
          position="relative"
          zIndex="1"
          flexDirection="column"
          alignItems="center"
          px="4x"
          pt={['8x', '12x', '12x']}
          pb={['12x', '8x', '8x']}
        >
          <Display
            size="400"
            textColor="primary_inverse"
            textAlign="center"
            mb="0x"
            sx={{ textShadow: '0 1px 5px rgba(0, 0, 0, 0.6)' }}
            webProps={{ as: 'h1', responsive: true }}
          >
            {headlineText}
          </Display>
          {subHeadlineText && (
            <Heading
              size="300"
              textColor="primary_inverse"
              textAlign="center"
              sx={{
                textShadow: '0 1px 5px rgba(0, 0, 0, 0.6)',
                maxWidth: '704px',
              }}
              webProps={{ as: 'h2' }}
            >
              {subHeadlineText}
            </Heading>
          )}
        </Flex>

        {/* Tablet/Desktop: search box inside the hero background */}
        <Flex
          display={['none', 'flex', 'flex']}
          position="relative"
          zIndex="1"
          flexDirection="column"
          alignItems="center"
          pb="12x"
        >
          <Box>
            <Box variant="elevation_mid">
              {hasRebookingProfiles ? (
                <Rebooking
                  sitters={rebookingProfiles}
                  services={petTypeBrowsableServiceTypeOptions}
                  language={language}
                />
              ) : (
                <SearchBoxContainer />
              )}
            </Box>
            {(shouldShowGroomingEntrypoints || shouldShowDogTrainingCard) && (
              <Box
                maxWidth={['768px', '768px', '850px']}
                width="100%"
                mt="4x"
                variant="elevation_mid"
              >
                {shouldShowGroomingEntrypoints ? <GroomingPilotCard /> : <DogTrainingPilotCard />}
              </Box>
            )}
          </Box>
          <Flex justifyContent="center">
            <Trustpilot languageCode={language} inverse />
          </Flex>
        </Flex>
      </Box>

      {/* Mobile: search card overlapping the hero */}
      <Box position="relative" zIndex="1" mt="-8x" display={['block', 'none', 'none']}>
        <NarrowDeviceSearchBoxContainer
          serviceLinksForCurrentRoute={serviceLinksForCurrentRoute}
          isAuthenticated={isAuthenticated ?? false}
        />
        {!isAuthenticated && (
          <Flex justifyContent="center">
            <Trustpilot languageCode={language} />
          </Flex>
        )}
      </Box>
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selector = (state: any) => ({
  petTypeBrowsableServiceTypeOptions:
    homePageSelectors.getPetTypeBrowsableServiceTypeOptions(state),
});

const HomePageHero = (): JSX.Element => (
  <Connect selector={selector}>
    {(connectedProps) => <HomePageHeroContent {...connectedProps} />}
  </Connect>
);

export default HomePageHero;
